import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, Alert,
  TextField, Typography,
} from '@mui/material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import CheckItem from '../shared/CheckItem';
import { eventYearFrom, formatEventDateRange } from '../shared/eventDate';
import usePageTitle from '../shared/hooks/usePageTitle';

const EMPTY = {
  mediaPermission: false,
  permissionConfirmed: false,
  parentName: '',
  email: '',
  signature: '',
  signatureDate: new Date().toISOString().split('T')[0],
  feeUnderstood: false,
  liabilityAgree: false,
};

export default function ParentConsent() {
  usePageTitle('OSSDF Parent Consent');
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('id') || '';

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [dancer, setDancer] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participationFee, setParticipationFee] = useState(null);
  const [eventYear, setEventYear] = useState('');
  const [eventDateRange, setEventDateRange] = useState('');

  const studentName = dancer ? `${dancer.firstName} ${dancer.lastName}` : 'Your child';

  // Pulled from the Events tab so the year/dates throughout this form don't
  // go stale every festival. Best-effort: falls back to no year/date range
  // shown rather than blocking the form.
  useEffect(() => {
    fetch(`${API_BASE}/api/register/event`)
      .then(res => (res.ok ? res.json() : null))
      .then(event => {
        setEventYear(eventYearFrom(event?.startDate));
        setEventDateRange(formatEventDateRange(event?.startDate, event?.endDate));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!participantId) {
      setLoadError('Missing participant ID. Please use the link provided to you.');
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/api/register/dancer/${encodeURIComponent(participantId)}`)
      .then(res => {
        if (!res.ok) throw new Error('Could not find this registration.');
        return res.json();
      })
      .then(data => {
        setDancer(data);
        setForm(prev => ({
          ...prev,
          parentName: data.parentGuardianName || '',
          email: data.parentGuardianEmail || '',
        }));
        setLoading(false);
      })
      .catch(err => { setLoadError(err.message); setLoading(false); });
  }, [participantId]);

  // Pulled from INFORMATION_SHEET's Prices tab ("Participation Fee" row) so
  // the fee shown here can't drift from what's actually charged — this is
  // the per-participant fee, separate from the school's one-time
  // registration fee. Best-effort: if it fails to load, the sentence below
  // just omits the dollar amount rather than blocking the whole form.
  useEffect(() => {
    fetch(`${API_BASE}/api/register/prices`)
      .then(res => (res.ok ? res.json() : null))
      .then(prices => setParticipationFee(prices?.['Participation Fee'] ?? null))
      .catch(() => {});
  }, []);

  const check = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.checked }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const text = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.mediaPermission) e.mediaPermission = 'Required';
    if (!form.parentName.trim()) e.parentName = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    if (!form.signature.trim()) e.signature = 'Required';
    if (!form.signatureDate) e.signatureDate = 'Required';
    if (!form.feeUnderstood) e.feeUnderstood = 'Required';
    if (!form.liabilityAgree) e.liabilityAgree = 'Required';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();

    console.log('validation errors:', e); // ADD THIS TEMPORARILY
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/api/register/parent-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          participantId,
          parentGuardianName: form.signature,
          parentGuardianEmail: form.email,
          parentConsentDate: form.signatureDate,
          mediaPermission: form.mediaPermission,
          feeUnderstood: form.feeUnderstood,
          liabilityAgree: form.liabilityAgree,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Server error ${res.status}`);
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (loadError) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Alert severity="error" sx={{ maxWidth: 480, mx: 'auto' }}>{loadError}</Alert>
    </Box>
  );

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
        <Typography variant="h5" fontWeight={700}>Consent submitted!</Typography>
        <Typography color="text.secondary">
          Thank you, {form.parentName}. The media release consent for <strong>{studentName}</strong> has been received.
        </Typography>
        <Button variant="outlined" href="https://ossdancefest.ca" sx={{ mt: 2 }}>
          Return to ossdancefest.ca
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4, gap: 1 }}>
          <FamilyRestroomIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="h4" fontWeight={700}>Parent / Guardian Consent</Typography>
          <Typography color="text.secondary">OSS Dancefest{eventYear ? ` ${eventYear}` : ''} — Media Release</Typography>
        </Box>

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              You are completing the media release consent form for:
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>{studentName}</Typography>
          </CardContent>
        </Card>

        {/* Parent name + email */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Parent/Guardian Name <Typography component="span" variant="body2" color="text.secondary">(or Participant Name if 18 years of age)</Typography>
            </Typography>
            <TextField label="Full name" required fullWidth size="small"
              value={form.parentName} onChange={text('parentName')}
              error={!!errors.parentName} helperText={errors.parentName} />
            <TextField label="Email address" required fullWidth size="small" type="email"
              value={form.email} onChange={text('email')}
              error={!!errors.email} helperText={errors.email} />
          </CardContent>
        </Card>

        {/* Media Release Information */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Media Release Information</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Our priority is to keep our participants' identities safe while offering an event that provides opportunity for all youth who choreograph and/or perform original dance compositions to share and celebrate their creative artistry with us and each other.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Please read the following media release form and complete all required fields. If you have questions or are unable to fill out the form, please email <strong>ossdancefest@gmail.com</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Note: All information in the Media Release Form will be kept confidential with OSSDF and not shared with other parties.
            </Typography>
          </CardContent>
        </Card>

        {/* Acknowledgements */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Acknowledgements *</Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I give Ontario Secondary School DANCEFEST (the "Media") permission to share the video-recorded dance entries and photographs of my child with the understanding that the video(s) of dance entries, recorded by our videographer, and the photographs taken by our photographer during the festival{eventDateRange ? ` from ${eventDateRange}` : ''}, will be shared with teachers following the event for teachers, students, and family members to view from their classroom or home.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In addition, DANCEFEST may share the dance entries in a virtual event with a password-protected link on our website, ossdancefest.ca. During the live event, entries will not be distributed outside of the event and its participants.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Furthermore, when photographs and other celebrated student work are released on the DANCEFEST website and social media platforms, DANCEFEST cannot control or prevent the further distribution or use of the material by those who access the content.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upon electronic signature of this form, participants agree not to record participants using a mobile or camera device at a live event. I understand that DANCEFEST will use the video submission(s) for the purposes of education, festival promotion, and on DANCEFEST's website and social media channels.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I acknowledge that the first name (no surname) and/or image of my child may be revealed in the Media or by descriptive text or commentary; however, DANCEFEST will not publish any address, telephone number, or email address with the Media without explicit permission.
            </Typography>
            <CheckItem checked={form.mediaPermission} onChange={check('mediaPermission')} error={errors.mediaPermission} label="I provide permission based on the above statement and will follow all requirements." />
          </CardContent>
        </Card>

        {/* Risk & liability acknowledgement */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Participant Risk and Liability Acknowledgement</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I understand that participation in Dancefest involves physical activity, including dance, movement, rehearsals, workshops, and performances, and that there are inherent risks of injury associated with these activities. I acknowledge and voluntarily accept these risks on behalf of myself/my child.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I understand that Dancefest does not provide individual accident, injury, or medical insurance for participants, and that families may choose to obtain private insurance coverage at their own expense.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I agree that Dancefest, its organizers, volunteers, adjudicators, host facilities, and affiliated personnel will not be held responsible for injuries, loss, or damage arising from participation in Dancefest.
            </Typography>
                       
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              By signing below, I confirm that I have read and understood this acknowledgement and give permission for myself/my child to participate in Dancefest activities.
            </Typography>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Thank you for your support in protecting the likenesses of our participants and your attendance to our OSSDF{eventYear ? ` ${eventYear}` : ''} Event.
            </Typography>
            <CheckItem checked={form.liabilityAgree} onChange={check('liabilityAgree')} error={errors.liabilityAgree} label="I have read and agree to the Participant Risk and Liability Acknowledgement." />
          </CardContent>
        </Card>

                {/* Fee acknowledgement */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Registration Fee</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I understand that my {participationFee != null ? `$${participationFee.toFixed(2)} ` : ''}registration fee, once paid, is non-refundable. If there are any questions, please contact Cathy Bowman at <strong>ossdfsecretary@gmail.com</strong>.
            </Typography>
            <CheckItem checked={form.feeUnderstood} onChange={check('feeUnderstood')} error={errors.feeUnderstood} label="I understand." />
          </CardContent>
        </Card>

                {/* Electronic signature */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Electronic Signature</Typography>
                        <Typography variant="body2" color="text.secondary">
              I certify that I am the parent or legal guardian of the individual named and I give my consent without reservation to the foregoing on their behalf. If the person named is over the age of 18, they may sign without the acknowledgement of a parent or legal guardian.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              By typing your name below, you provide your electronic signature for the Media Release:
            </Typography>
            <TextField label="Parent/Guardian name (electronic signature)" required fullWidth size="small"
              value={form.signature} onChange={text('signature')}
              error={!!errors.signature} helperText={errors.signature} />

            <Typography variant="body2" color="text.secondary">
              By entering the date below, you certify the electronic signature for the Media Release:
            </Typography>
            <TextField label="Date" required fullWidth size="small" type="date"
              value={form.signatureDate} onChange={text('signatureDate')}
              InputLabelProps={{ shrink: true }}
              error={!!errors.signatureDate} helperText={errors.signatureDate} />
          </CardContent>
        </Card>

        {submitError && <Alert severity="error" sx={{ mb: 3 }}>{submitError}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit consent'}
          </Button>
        </Box>

      </Box>
    </Box>
  );
}
